import React from 'react';
import { Component } from 'react';
import ClientsList, { ClientsListPersistentState, DEFAULT_CLIENTS_LIST_STATE } from './ClientsList';
import { ClientRecordView } from './ClientRecordView';
import {
    AssessmentDefinition,
    Client,
    ClientActivityPoint,
    ClientCustomProperty,
    SubscribeClientBriefing,
    SubscribeClientContent,
    ClientPlanCatalogue,
    ClientPlanOverride,
    GeneratedClientPlan,
    Nullable,
    ReportTemplate,
    SessionRecordingClient,
    Therapist,
    TherapySession,
    TherapySessionPlacedTag,
    TherapySessionReport,
    TherapySessionReportGenerationRequest,
    TherapySessionTagDefinition,
    TherapySessionTranscriptUtterance,
    TimelineFileUploadOptions,
    TimelineDocument,
    SpeechNeed,
    Condition,
    FamilySponsorshipStatus,
    UserResponseRecord,
    UserType,
} from '../interfaces/Frameworks';
import { 
    TabsItemProps, 
    TabsView, 
} from './TabsView';
import { Theme } from '../Theme';
import { Button } from './Button';
import { formatPhoneNumberForClientStorage, PhoneNumberInput } from './PhoneNumberInput';
import { read } from 'xlsx';
import { ClientsImportEntry, ClientsImportResult, parseClientsImportCsv } from './ClientsImport';
import { parseClientsImportWorkbook } from './ClientsImportWorkbook';
import { getClientSpeechNeeds, withClientSpeechNeeds } from '../clientSpeechNeeds';
import { getRequiredAdditionalLicenses } from '../saas/licenseUsage';

type Props = {
    authenticatedUserType: UserType;
    authenticatedUserId: string;
    usedLicenses?: number;
    mobileAppUsageStatus?: 'loading' | 'ready' | 'error';
    totalLicenses?: number;
    licenseUsage?: import('../interfaces/Frameworks').LicenseUsageSummary;
    showFamilySponsorship: boolean;
    familySponsorshipStatus: FamilySponsorshipStatus | null;
    isLoadingFamilySponsorship: boolean;
    familySponsorshipError: string;
    canManageFamilySponsorship: boolean;
    allClients: Array<Client>;
    clients: Array<Client>;
    activityByClientId: Record<string, Array<number>>;
    activityTimelineByClientId: Record<string, Array<ClientActivityPoint>>;
    sessionMinutesTimelineByClientId: Record<string, Array<ClientActivityPoint>>;
    signUpTimeByClientId: Record<string, string>;
    stageByClientId: Record<string, string>;
    assessmentDefinitions: Array<AssessmentDefinition>;
    selectedClientAssessmentResponses: Array<UserResponseRecord>;
    isLoadingSelectedClientAssessments: boolean;
    therapySessions: Array<TherapySession>;
    therapySessionReports: Array<TherapySessionReport>;
    timelineDocuments: Array<TimelineDocument>;
    canUseAudioScribeAndReports: boolean;
    canUseClientPlans: boolean;
    canViewBriefingDiagnostics?: boolean;
    onSubscribeClientBriefing?: SubscribeClientBriefing;
    onSubscribeClientContent?: SubscribeClientContent;
    onLoadClientPlanCatalogue?: (clientId: string) => Promise<ClientPlanCatalogue>;
    onSubmitClientPlanOverride?: (clientId: string, goalIds: string[]) => Promise<ClientPlanOverride>;
    onGenerateClientPlan?: (clientId: string, instructions: string) => Promise<GeneratedClientPlan>;
    hasUsedTherapyApp?: boolean;
    onRequestClientBriefing?: (clientId: string, force?: boolean) => Promise<{ status: string }>;
    therapists: Array<Therapist>;
    currentTherapist: Nullable<Therapist>;
    reportTemplates: Array<ReportTemplate>;
    commsCampaignIds: Array<string>;
    defaultCommsCampaignId?: string;
    speechNeeds: Array<SpeechNeed>;
    healthcareProviderName: string;
    healthcareProviderLogoUrl?: string;
    conditions: Array<Condition>;
    onSelectClient: (client: Client) => void;
    onClientRecordChange?: (client: Nullable<Client>) => void;
    onSetFamilySponsorship: (sponsored: boolean) => Promise<void>;
    onImportSessionFile: (
        client: SessionRecordingClient,
        file: File,
        options?: TimelineFileUploadOptions
    ) => Promise<string>;
    onSaveSessionTitle: (sessionId: string, title: string) => Promise<void>;
    onSaveSessionDate: (sessionId: string, date: Date) => Promise<void>;
    onSaveSessionClinicalNotes: (sessionId: string, clinicalNotes: string) => Promise<void>;
    onSaveSessionTranscriptUtterances: (sessionId: string, utterances: Array<TherapySessionTranscriptUtterance>) => Promise<void>;
    onSaveSessionPlacedTags: (
        sessionId: string,
        placedTags: Array<TherapySessionPlacedTag>,
        previousPlacedTags?: Array<TherapySessionPlacedTag>
    ) => Promise<void>;
    onSaveTherapistSessionTags: (therapistId: string, tags: Array<TherapySessionTagDefinition>) => Promise<void>;
    onEnsureSessionWaveform: (sessionId: string) => Promise<void>;
    onRetrySessionTranscription?: (sessionId: string) => Promise<void>;
    onGenerateSessionReport: (request: TherapySessionReportGenerationRequest) => Promise<string>;
    onSaveSessionReportText: (reportId: string, reportText: string) => Promise<void>;
    onDeleteSessionReport: (report: TherapySessionReport) => Promise<void>;
    onDeleteSession: (session: TherapySession) => Promise<void>;
    onCreateTimelineClinicalNote: (document: Omit<TimelineDocument, 'id' | 'kind'>) => Promise<string>;
    onSaveTimelineClinicalNote: (documentId: string, clinicalNotes: string) => Promise<void>;
    onSaveTimelineDocumentTitle: (documentId: string, title: string) => Promise<void>;
    onSaveTimelineDocumentDate: (documentId: string, date: Date) => Promise<void>;
    onUploadTimelineFile: (
        document: Omit<TimelineDocument, 'id' | 'kind' | 'storagePath' | 'originalName' | 'contentType' | 'sizeBytes'>,
        file: File,
        options?: TimelineFileUploadOptions
    ) => Promise<string>;
    onGetTimelineFileDownloadUrl: (document: TimelineDocument) => Promise<string>;
    onDownloadTimelineFile: (document: TimelineDocument) => Promise<void>;
    onDeleteTimelineDocument: (document: TimelineDocument) => Promise<void>;
    onAddClient: (client: Client, carers: Client[]) => void;
    onImportClients: (entries: ClientsImportEntry[]) => Promise<ClientsImportResult>;
    onUpdateClient: (client: Client) => void;
    onDeleteClient?: (client: Client) => void;
};

type State = {
    selectedClient: Nullable<Client>;
    mode: 'clients' | 'clientRecord';
    isEditing: boolean;
    editingClientData: Nullable<Client>;
    hasConfirmedSpeechNeedsEditWarning: boolean;
    selectedTabsItemIndex: number;
    clientsListState: ClientsListPersistentState;
    showAddClientDialog: boolean;
    isImporting: boolean;
    newClientData: {
        givenName: string;
        surname: string;
        dateOfBirth: string;
        speechNeeds: string[];
        nhsNumber: string;
        notes: string;
        
        carers: Array<{
            commsCampaignId: string;
            email: string;
            firstName: string;
            lastName: string;
            phoneNumber: string;
            notes: string;
        }>;
    };
};

export class ClientsView extends Component<Props, State> {

    // Only the record actually visible in this view is a default recording participant.
    componentDidMount() {
        this.publishClientRecordContext();
    }

    componentDidUpdate(_previousProps: Props, previousState: State) {
        if (previousState.selectedClient !== this.state.selectedClient || previousState.mode !== this.state.mode) {
            this.publishClientRecordContext();
        }
    }

    componentWillUnmount() {
        this.props.onClientRecordChange?.(null);
    }

    private publishClientRecordContext() {
        this.props.onClientRecordChange?.(this.state.mode === 'clientRecord' ? this.state.selectedClient : null);
    }

    constructor(props: Props) {
        super(props);
        this.state = {
            selectedClient: null,
            mode: 'clients',
            isEditing: false,
            editingClientData: null,
            hasConfirmedSpeechNeedsEditWarning: false,
            selectedTabsItemIndex: 0,
            clientsListState: { ...DEFAULT_CLIENTS_LIST_STATE },
            showAddClientDialog: false,
            isImporting: false,
            newClientData: {
                givenName: '',
                surname: '',
                dateOfBirth: '',
                speechNeeds: [],
                nhsNumber: '',
                notes: '',
                carers: [{
                    commsCampaignId: '',
                    firstName: '',
                    lastName: '',
                    email: '',
                    phoneNumber: '',
                    notes: '',
                }],
            },
        };
    }

    private tabs: Array<TabsItemProps> = [
        {
            label: 'All clients',
            id: 'all_clients',
            onSelect: () => {
                this.setState({ selectedTabsItemIndex: 0 });
            },
        },
        {
            label: 'My clients',
            id: 'my_clients',
            onSelect: () => {
                this.setState({ selectedTabsItemIndex: 1 });
            },
        },
    ];

    private showAddClientDialog = () => {
        this.setState({ showAddClientDialog: true });
    }

    private hideAddClientDialog = () => {
        this.setState({ 
            showAddClientDialog: false,
            newClientData: {
                givenName: '',
                surname: '',
                dateOfBirth: '',
                speechNeeds: [],
                nhsNumber: '',
                notes: '',
                carers: [{
                    commsCampaignId: '',
                    firstName: '',
                    lastName: '',
                    email: '',
                    phoneNumber: '',
                    notes: '',
                }],
            },
        });
    }

    private handleInputChange = (field: keyof State['newClientData']) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        this.setState({
            newClientData: {
                ...this.state.newClientData,
                [field]: e.target.value,
            }
        });
    }

    private getSpeechNeedsAfterToggle(currentSpeechNeeds: string[], speechNeedId: string, isSelected: boolean): string[] {
        const selectedSpeechNeeds = new Set(currentSpeechNeeds);
        if (isSelected) {
            selectedSpeechNeeds.add(speechNeedId);
        } else {
            selectedSpeechNeeds.delete(speechNeedId);
        }

        return this.props.speechNeeds
            .map((speechNeed) => speechNeed.id)
            .filter((id) => selectedSpeechNeeds.has(id));
    }

    private handleNewClientSpeechNeedToggle = (speechNeedId: string, isSelected: boolean) => {
        this.setState({
            newClientData: {
                ...this.state.newClientData,
                speechNeeds: this.getSpeechNeedsAfterToggle(this.state.newClientData.speechNeeds, speechNeedId, isSelected),
            }
        });
    }

    private handleCarerPhoneNumberChange = (index: number, phoneNumber: string) => {
        // Keep newly-created carer phone numbers stored with the selected international dial code.
        const newCarers = [...this.state.newClientData.carers];
        newCarers[index] = {
            ...newCarers[index],
            phoneNumber,
        };
        this.setState({
            newClientData: {
                ...this.state.newClientData,
                carers: newCarers,
            }
        });
    }

    private handleCarerInputChange = (index: number, field: 'firstName' | 'lastName' | 'email' | 'phoneNumber' | 'notes' | 'commsCampaignId') => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const newCarers = [...this.state.newClientData.carers];
        newCarers[index] = {
            ...newCarers[index],
            [field]: e.target.value,
        };
        this.setState({
            newClientData: {
                ...this.state.newClientData,
                carers: newCarers,
            }
        });
    }

    private handleAddCarer = () => {
        this.setState({
            newClientData: {
                ...this.state.newClientData,
                    carers: [
                    ...this.state.newClientData.carers,
                    {
                        commsCampaignId: '',
                        firstName: '',
                        lastName: '',
                        email: '',
                        phoneNumber: '',
                        notes: '',
                    }
                ]
            }
        });
    }

    private handleRemoveCarer = (index: number) => () => {
        const newCarers = [...this.state.newClientData.carers];
        newCarers.splice(index, 1);
        this.setState({
            newClientData: {
                ...this.state.newClientData,
                carers: newCarers,
            }
        });
    }

    private handleCreateClient = () => {
        const { newClientData } = this.state;
        const carers: Client[] = newClientData.carers.map(c => ({
            userType: 'carer' as const,
            givenName: c.firstName,
            familyName: c.lastName,
            email: c.email,
            phoneNumber: formatPhoneNumberForClientStorage(c.phoneNumber),
            notes: c.notes,
            ...(c.commsCampaignId ? { commsCampaignId: c.commsCampaignId } : {}),
        }));
        
        const client: Client = withClientSpeechNeeds({
            userType: 'care_recipient' as const,
            givenName: newClientData.givenName,
            familyName: newClientData.surname,
            dateOfBirth: newClientData.dateOfBirth,
            nhsNumber: newClientData.nhsNumber,
            conditionIds: [],
            speechNeeds: newClientData.speechNeeds,
            notes: newClientData.notes,
        });
        this.props.onAddClient(client, carers);
        this.hideAddClientDialog();
    }

    private handleEditClientChange = (field: keyof Client, value: string) => {
        if (this.state.editingClientData) {
            this.setState({
                editingClientData: {
                    ...this.state.editingClientData,
                    [field]: value,
                }
            });
        }
    }

    // Keep custom-property edits in an independent draft so Discard preserves the saved record.
    private handleEditClientCustomPropertiesChange = (customProperties: ClientCustomProperty[]) => {
        this.setState((state) => state.editingClientData ? {
            editingClientData: {
                ...state.editingClientData,
                customProperties: customProperties.map((property) => ({ ...property })),
            },
        } : null);
    }

    private handleEditClientSpeechNeedsChange = (speechNeeds: string[]) => {
        if (this.state.editingClientData) {
            if (!this.state.hasConfirmedSpeechNeedsEditWarning) {
                const shouldChangeSpeechNeeds = window.confirm(
                    'Changing this client\'s support areas means they will need to go through the AI planner assessment again the next time they launch the app.\n\nSelect OK to make this change, or Cancel to revert this selection.'
                );

                if (!shouldChangeSpeechNeeds) {
                    return;
                }
            }

            this.setState({
                editingClientData: withClientSpeechNeeds({
                    ...this.state.editingClientData,
                    speechNeeds,
                }),
                hasConfirmedSpeechNeedsEditWarning: true,
            });
        }
    }

    private renderSpeechNeedsCheckboxList(
        selectedSpeechNeeds: string[],
        onToggleSpeechNeed: (speechNeedId: string, isSelected: boolean) => void
    ): React.ReactNode {
        const selectedSpeechNeedIds = new Set(selectedSpeechNeeds);

        return (
            <div style={styles.speechNeedsCheckboxList}>
                {this.props.speechNeeds.map((speechNeed) => (
                    <label key={speechNeed.id} style={styles.speechNeedCheckboxLabel}>
                        <input
                            type='checkbox'
                            checked={selectedSpeechNeedIds.has(speechNeed.id)}
                            onChange={(event) => onToggleSpeechNeed(speechNeed.id, event.currentTarget.checked)}
                            style={styles.checkbox}
                        />
                        <span>{speechNeed.name}</span>
                    </label>
                ))}
            </div>
        );
    }

    private handleCommsCampaignEnrollmentChange = (isEnrolled: boolean) => {
        if (!this.state.editingClientData) {
            return;
        }

        this.setState({
            editingClientData: {
                ...this.state.editingClientData,
                commsCampaignId: isEnrolled ? this.props.defaultCommsCampaignId : undefined,
            }
        });
    }

    private sanitiseClient(client: Client): Client {
        if (client.displayName && !client.givenName && !client.familyName) {
            const newClient = {
                ...client,
                givenName: client.displayName,
                familyName: '',
            };
            delete newClient.displayName;
            return withClientSpeechNeeds(newClient);
        }
        return withClientSpeechNeeds({...client, notes: client.notes || ''});
    }

    private handleSaveClient = () => {
        const { editingClientData } = this.state;
        if (editingClientData) {
            // Normalize labels while preserving free text and leaving legacy records without extra fields.
            const clientToSave = {
                ...editingClientData,
                ...(editingClientData.phoneNumber ? {
                    phoneNumber: formatPhoneNumberForClientStorage(editingClientData.phoneNumber),
                } : {}),
                ...(editingClientData.customProperties !== undefined ? {
                    customProperties: editingClientData.customProperties.map((property) => ({
                        ...property,
                        name: property.name.trim(),
                    })),
                } : {}),
            };
            this.props.onUpdateClient(clientToSave);
            this.setState({ 
                selectedClient: clientToSave,
                isEditing: false, 
                editingClientData: null,
                hasConfirmedSpeechNeedsEditWarning: false,
            });
        }
    }

    private handleImport = async (file: File) => {
        try {
            const importOptions = {
                defaultCommsCampaignId: this.props.defaultCommsCampaignId,
            };
            const lowerCaseFileName = file.name.toLowerCase();

            let entries: ClientsImportEntry[];
            if (lowerCaseFileName.endsWith('.xlsx')) {
                // Preserve Excel number formats so genuine date cells can be normalized independently of locale.
                const workbook = read(await file.arrayBuffer(), { type: 'array', cellNF: true });
                entries = parseClientsImportWorkbook(workbook, this.props.speechNeeds, importOptions);
            } else {
                const csvText = await file.text();
                entries = parseClientsImportCsv(csvText, this.props.speechNeeds, importOptions);
            }

            if (entries.length === 0) {
                window.alert('No importable client rows were found. Fill in the Clients sheet of the template, then upload it again.');
                return;
            }

            const totalCarers = entries.reduce((count, entry) => count + entry.carers.length, 0);
            const confirmed = window.confirm(
                `Import ${entries.length} care recipient row(s) and ${totalCarers} carer record(s) from ${file.name}?`
            );

            if (!confirmed) {
                return;
            }

            this.setState({ isImporting: true });
            const result = await this.props.onImportClients(entries);
            window.alert(
                `Import complete. Added ${result.importedClients} care recipient(s), created ${result.importedCarers} new carer record(s), and reused ${result.reusedCarers} existing carer record(s) from ${result.importedRows} spreadsheet row(s).`
            );
        } catch (error) {
            console.error('CLIENTS_VIEW: handleImport(): Error importing spreadsheet:', error);
            const message = error instanceof Error ? error.message : 'Failed to import clients from the spreadsheet.';
            window.alert(message);
        } finally {
            this.setState({ isImporting: false });
        }
    }

    renderClientsList() {
        const visibleClients = this.state.selectedTabsItemIndex === 1
            ? this.props.clients.filter(client => client.therapistUserId === this.props.authenticatedUserId)
            : this.props.clients;

        return (
            <div style={styles.mainContent}>
                {this.props.licenseUsage ? this.renderSaasLicenseSummary() : (
                    <div
                        style={styles.licensesSummary}
                        role="status"
                        title="A family is counted when a linked carer first uses the mobile app. Unclaimed referrals are not counted."
                    >
                        {/* Show unavailable evidence explicitly, reserving zero for a successful activity lookup. */}
                        Families using the app: {this.props.mobileAppUsageStatus === 'error' ? 'Unable to load' :
                            this.props.mobileAppUsageStatus !== 'ready' ? 'Loading…' : this.props.usedLicenses ?? 'Unavailable'}
                        {this.props.mobileAppUsageStatus === 'ready' && this.props.usedLicenses != null &&
                            this.props.totalLicenses != null ? ` / ${this.props.totalLicenses}` : ''}
                    </div>
                )}
                <TabsView options={this.tabs} selectedTabsItemIndex={this.state.selectedTabsItemIndex} />

                {/* left panel */}
                <div id='leftPanel' style={styles.leftPanel}>

                    {/* clients list */}
                    <div style={{ flexGrow: 1, display: 'flex', minHeight: 0 }}>
                        <ClientsList
                            authenticatedUserType={this.props.authenticatedUserType}
                            clients={visibleClients}
                            activityByClientId={this.props.activityByClientId}
                            stageByClientId={this.props.stageByClientId}
                            therapists={this.props.therapists}
                            currentTherapist={this.props.currentTherapist}
                            speechNeeds={this.props.speechNeeds}
                            healthcareProviderName={this.props.healthcareProviderName}
                            healthcareProviderLogoUrl={this.props.healthcareProviderLogoUrl}
                            importButtonLabel={this.state.isImporting ? 'Importing...' : 'Import'}
                            isImporting={this.state.isImporting}
                            onAddClient={this.showAddClientDialog}
                            onImport={this.handleImport}
                            onSelectClient={(client) => {
                                this.props.onSelectClient(client);
                                this.setState({
                                    selectedClient: client,
                                    mode: 'clientRecord',
                                    isEditing: false,
                                    editingClientData: null,
                                    hasConfirmedSpeechNeedsEditWarning: false,
                                });
                            }}
                            selectedClient={this.state.selectedClient}
                            listState={this.state.clientsListState}
                            onListStateChange={(clientsListState) => this.setState({ clientsListState })}
                        />
                    </div>
                </div>
            </div>
        );
    }

    // Show signed SaaS allowance usage while retaining the old Enterprise display above.
    private renderSaasLicenseSummary(): React.ReactNode {
        const usage = this.props.licenseUsage!;
        const startsAt = this.formatLicenseDate(usage.periodStartsAt);
        const endsAt = this.formatLicenseDate(usage.periodEndsAt);
        const allowance = usage.includedLicenses;
        const overage = getRequiredAdditionalLicenses(usage);

        return (
            <div
                style={overage > 0 ? styles.licensesSummaryOverage : styles.licensesSummary}
                role={overage > 0 ? 'alert' : undefined}
            >
                <strong>
                    {usage.licenseModel === 'annual_activation' ?
                        `Used${startsAt ? ` since ${startsAt}` : ' this license year'}: ` :
                        'Used licenses: '}
                    {usage.usedLicensesSinceAnnualRenewalDate} / {allowance}
                </strong>
                <span>Licenses available: {usage.availableLicenses}</span>
                {usage.licenseModel === 'annual_activation' && (
                    <span>
                        Licenses available per year: {usage.numberOfLicensesAvailablePerYear ?? usage.includedLicenses}
                    </span>
                )}
                <span>{usage.includedLicenses} included in the current subscription</span>
                {endsAt && <span>Renews {endsAt}</span>}
                <span title="Currently sponsored families with recorded mobile-app use by a linked carer.">
                    Families using the app: {usage.currentlySponsoredFamilies}
                </span>
                {overage > 0 && (
                    <strong>Increase Premium capacity by at least {Math.ceil(overage / 50)} subscription unit{Math.ceil(overage / 50) === 1 ? '' : 's'}.</strong>
                )}
            </div>
        );
    }

    private formatLicenseDate(value: any): string {
        if (!value) {
            return '';
        }
        const rawValue = typeof value?.toDate === 'function' ? value.toDate() : value;
        const date = rawValue instanceof Date ? rawValue : new Date(rawValue);
        return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-GB');
    }

    render(): React.ReactNode {
        const content = (() => {
            switch (this.state.mode) {
                case 'clients': {
                    return this.renderClientsList();
                }
                case 'clientRecord': {
                    return this.renderClientRecord();
                }
                default: {
                    return <div>Select a client to view their record.</div>;
                }
            }
        })();

        return (
            <>
                {content}
                {this.renderAddClientDialog()}
            </>
        );
    }

    private renderAddClientDialog(): React.ReactNode {
        if (!this.state.showAddClientDialog) {
            return null;
        }

        const { newClientData } = this.state;
        const { commsCampaignIds } = this.props;

        const isCreateDisabled = !newClientData.givenName ||
            !newClientData.surname ||
            !newClientData.dateOfBirth ||
            newClientData.carers.some(c => !c.firstName || !c.lastName || !c.phoneNumber);

        return (
            <div style={styles.dialogOverlay}>
                <div style={styles.dialog}>
                    <h3 style={styles.dialogTitle}>Give access</h3>
                    
                    {/* Client Details Section */}
                    <div style={styles.section}>
                        <h4 style={styles.sectionTitle}>Care recipient details</h4>
                        
                        <div style={styles.formField}>
                            <label style={styles.label}>First name*</label>
                            <input
                                type='text'
                                value={newClientData.givenName}
                                onChange={this.handleInputChange('givenName')}
                                style={styles.input}
                            />
                        </div>

                        <div style={styles.formField}>
                            <label style={styles.label}>Last name*</label>
                            <input
                                type='text'
                                value={newClientData.surname}
                                onChange={this.handleInputChange('surname')}
                                style={styles.input}
                            />
                        </div>

                        <div style={styles.formField}>
                            <label style={styles.label}>Date of birth*</label>
                            <input
                                type='date'
                                value={newClientData.dateOfBirth}
                                onChange={this.handleInputChange('dateOfBirth')}
                                style={styles.input}
                            />
                        </div>

                        <div style={styles.formField}>
                            <label style={styles.label}>Support areas</label>
                            {this.renderSpeechNeedsCheckboxList(
                                getClientSpeechNeeds(newClientData),
                                this.handleNewClientSpeechNeedToggle
                            )}
                        </div>

                        <div style={styles.formField}>
                            <label style={styles.label}>Notes</label>
                            <textarea
                                value={newClientData.notes}
                                onChange={this.handleInputChange('notes')}
                                style={{...styles.input, marginTop: '4px', marginLeft: '16px', height: '80px', resize: 'vertical'}}
                            />
                        </div>
                    </div>

                    {/* Carer Details Section */}
                    <div style={styles.section}>
                        <h4 style={styles.sectionTitle}>Carer details</h4>
                        
                        {newClientData.carers.map((carer, index) => (
                            <div key={index} style={{ marginBottom: index < newClientData.carers.length - 1 ? '24px' : '0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', marginTop: index > 0 ? '16px' : '0' }}>
                                    <h5 style={{ ...styles.sectionTitle, margin: 0, fontSize: '14px' }}>Carer {index + 1}</h5>
                                    <Button onClick={this.handleRemoveCarer(index)} label='' icon={require('./assets/icons/close_normal.svg').default} type='secondary' style={{ padding: '8px', height: 'auto', minWidth: 'auto' }} />
                                </div>
                                
                                <div style={styles.formField}>
                                    <label style={styles.label}>First name*</label>
                                    <input
                                        type='text'
                                        value={carer.firstName}
                                        onChange={this.handleCarerInputChange(index, 'firstName')}
                                        style={styles.input}
                                    />
                                </div>

                                <div style={styles.formField}>
                                    <label style={styles.label}>Last name*</label>
                                    <input
                                        type='text'
                                        value={carer.lastName}
                                        onChange={this.handleCarerInputChange(index, 'lastName')}
                                        style={styles.input}
                                    />
                                </div>

                                <div style={{...styles.formField}}>
                                    <label style={styles.label}>Phone number*</label>
                                    <PhoneNumberInput
                                        value={carer.phoneNumber}
                                        onChange={(phoneNumber) => this.handleCarerPhoneNumberChange(index, phoneNumber)}
                                        required={true}
                                        containerStyle={styles.createClientPhoneInputGroup}
                                    />
                                </div>

                                <div style={styles.formField}>
                                    <label style={styles.label}>Email</label>
                                    <input
                                        type='email'
                                        value={carer.email}
                                        onChange={this.handleCarerInputChange(index, 'email')}
                                        style={styles.input}
                                    />
                                </div>

                                <div style={styles.formField}>
                                    <label style={styles.label}>Notes</label>
                                    <textarea
                                        value={carer.notes}
                                        onChange={this.handleCarerInputChange(index, 'notes')}
                                        style={{...styles.input, marginTop: '4px', marginLeft: '16px', height: '80px', resize: 'vertical'}}
                                    />
                                </div>

                                <div style={styles.formField}>
                                    <label style={styles.label}>Comms campaign</label>
                                    <select
                                        value={carer.commsCampaignId}
                                        onChange={this.handleCarerInputChange(index, 'commsCampaignId')}
                                        style={styles.select}
                                    >
                                        <option value=''>None</option>
                                        {commsCampaignIds.map((campaignId) => (
                                            <option key={campaignId} value={campaignId}>
                                                {campaignId}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        ))}
                        
                        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-start' }}>
                            <Button onClick={this.handleAddCarer} label='Add carer' icon={require('./assets/icons/add_normal.svg').default} type='secondary' />
                        </div>
                    </div>

                    <p style={styles.requiredFields}>* Required fields</p>
                    
                    {/* <div style={styles.checkboxContainer}>
                        <label style={styles.checkboxLabel}>
                            <input type='checkbox' style={styles.checkbox} />
                            Notify carer(s) about SuperPenguin access
                        </label>
                    </div>
                    
                    <div style={styles.checkboxContainer}>
                        <label style={styles.checkboxLabel}>
                            <input type='checkbox' style={styles.checkbox} />
                            Send reminders to carer(s) if SuperPenguin account is not activated
                        </label>
                    </div> */}
                    
                    <div style={styles.dialogButtons}>
                        <Button onClick={this.hideAddClientDialog} label='Cancel' type='secondary' />
                        <Button onClick={this.handleCreateClient} label='Create' type='primary' disabled={isCreateDisabled} />
                    </div>
                </div>
            </div>
        );
    }

    private renderClientRecord(): React.ReactNode {
        const { selectedClient, isEditing, editingClientData } = this.state;
        
        if (!selectedClient) {
            return null;
        }

        return (
            <ClientRecordView
                authenticatedUserType={this.props.authenticatedUserType}
                authenticatedUserId={this.props.authenticatedUserId}
                selectedClient={selectedClient}
                allClients={this.props.allClients}
                activityTimelineByClientId={this.props.activityTimelineByClientId}
                sessionMinutesTimelineByClientId={this.props.sessionMinutesTimelineByClientId}
                signUpTimeByClientId={this.props.signUpTimeByClientId}
                stageByClientId={this.props.stageByClientId}
                assessmentDefinitions={this.props.assessmentDefinitions}
                selectedClientAssessmentResponses={this.props.selectedClientAssessmentResponses}
                isLoadingSelectedClientAssessments={this.props.isLoadingSelectedClientAssessments}
                therapySessions={this.props.therapySessions}
                therapySessionReports={this.props.therapySessionReports}
                timelineDocuments={this.props.timelineDocuments}
                canUseAudioScribeAndReports={this.props.canUseAudioScribeAndReports}
                canUseClientPlans={this.props.canUseClientPlans}
                canViewBriefingDiagnostics={this.props.canViewBriefingDiagnostics}
                onSubscribeClientBriefing={this.props.onSubscribeClientBriefing}
                onSubscribeClientContent={this.props.onSubscribeClientContent}
                onLoadClientPlanCatalogue={this.props.onLoadClientPlanCatalogue}
                onSubmitClientPlanOverride={this.props.onSubmitClientPlanOverride}
                onGenerateClientPlan={this.props.onGenerateClientPlan}
                hasUsedTherapyApp={this.props.hasUsedTherapyApp}
                onRequestClientBriefing={this.props.onRequestClientBriefing}
                therapists={this.props.therapists}
                currentTherapist={this.props.currentTherapist}
                reportTemplates={this.props.reportTemplates}
                speechNeeds={this.props.speechNeeds}
                isEditing={isEditing}
                editingClientData={editingClientData}
                defaultCommsCampaignId={this.props.defaultCommsCampaignId}
                showFamilySponsorship={this.props.showFamilySponsorship}
                familySponsorshipStatus={this.props.familySponsorshipStatus}
                isLoadingFamilySponsorship={this.props.isLoadingFamilySponsorship}
                familySponsorshipError={this.props.familySponsorshipError}
                canManageFamilySponsorship={this.props.canManageFamilySponsorship}
                onBack={() => this.setState({
                    selectedClient: null,
                    mode: 'clients',
                    isEditing: false,
                    editingClientData: null,
                    hasConfirmedSpeechNeedsEditWarning: false,
                })}
                onImportSessionFile={this.props.onImportSessionFile}
                onSaveSessionTitle={this.props.onSaveSessionTitle}
                onSaveSessionDate={this.props.onSaveSessionDate}
                onSaveSessionClinicalNotes={this.props.onSaveSessionClinicalNotes}
                onSaveSessionTranscriptUtterances={this.props.onSaveSessionTranscriptUtterances}
                onSaveSessionPlacedTags={this.props.onSaveSessionPlacedTags}
                onSaveTherapistSessionTags={this.props.onSaveTherapistSessionTags}
                onEnsureSessionWaveform={this.props.onEnsureSessionWaveform}
                onRetrySessionTranscription={this.props.onRetrySessionTranscription}
                onGenerateSessionReport={this.props.onGenerateSessionReport}
                onSaveSessionReportText={this.props.onSaveSessionReportText}
                onDeleteSessionReport={this.props.onDeleteSessionReport}
                onDeleteSession={this.props.onDeleteSession}
                onCreateTimelineClinicalNote={this.props.onCreateTimelineClinicalNote}
                onSaveTimelineClinicalNote={this.props.onSaveTimelineClinicalNote}
                onSaveTimelineDocumentTitle={this.props.onSaveTimelineDocumentTitle}
                onSaveTimelineDocumentDate={this.props.onSaveTimelineDocumentDate}
                onUploadTimelineFile={this.props.onUploadTimelineFile}
                onGetTimelineFileDownloadUrl={this.props.onGetTimelineFileDownloadUrl}
                onDownloadTimelineFile={this.props.onDownloadTimelineFile}
                onDeleteTimelineDocument={this.props.onDeleteTimelineDocument}
                onStartEditing={() => {
                    const sanitisedClient = this.sanitiseClient({ ...selectedClient });
                    this.setState({
                        isEditing: true,
                        editingClientData: sanitisedClient,
                        hasConfirmedSpeechNeedsEditWarning: false,
                    });
                }}
                onDiscardEditing={() => this.setState({
                    isEditing: false,
                    editingClientData: null,
                    hasConfirmedSpeechNeedsEditWarning: false,
                })}
                onSaveClient={this.handleSaveClient}
                onAssignMyClient={() => {
                    const updatedClient = {
                        ...selectedClient,
                        therapistUserId: this.props.authenticatedUserId,
                    };
                    this.props.onUpdateClient(updatedClient);
                    this.setState({
                        selectedClient: updatedClient,
                        isEditing: false,
                        editingClientData: null,
                        hasConfirmedSpeechNeedsEditWarning: false,
                    });
                }}
                onEditClientChange={this.handleEditClientChange}
                onEditClientCustomPropertiesChange={this.handleEditClientCustomPropertiesChange}
                onEditClientSpeechNeedsChange={this.handleEditClientSpeechNeedsChange}
                onCommsCampaignEnrollmentChange={this.handleCommsCampaignEnrollmentChange}
                onSelectClient={(client) => {
                    this.props.onSelectClient(client);
                    this.setState({
                        selectedClient: client,
                        mode: 'clientRecord',
                        isEditing: false,
                        editingClientData: null,
                        hasConfirmedSpeechNeedsEditWarning: false,
                    });
                }}
                onSetFamilySponsorship={this.props.onSetFamilySponsorship}
                onDeleteClient={this.props.onDeleteClient}
            />
        );
    }
}

const styles = {
    mainContent: {
        flexDirection: 'column' as const,
        display: 'flex',
        flexGrow: 1,
        minHeight: 0,
        minWidth: 0,
        backgroundColor: Theme.Colours.background,
        overflow: 'hidden' as const,
    },
    leftPanel: {
        display: 'flex',
        flexDirection: 'column' as const,
        flex: 1,
        width: '100%',
        minHeight: 0,
        minWidth: 0,
        borderRight: '1px solid #999',
        position: 'relative' as const,
        overflow: 'hidden',
    },
    licensesSummary: {
        ...Theme.Fonts.body,
        padding: '20px 32px 12px 32px',
        color: Theme.Colours.text,
        flexShrink: 0,
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: '8px 18px',
    },
    licensesSummaryOverage: {
        ...Theme.Fonts.body,
        margin: '14px 28px 6px 28px',
        padding: '12px 14px',
        color: '#8a1c12',
        backgroundColor: '#fff0ed',
        border: '1px solid #e6a39b',
        borderRadius: '8px',
        flexShrink: 0,
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: '8px 18px',
    },
    contentPanel: {
        flexGrow: 1,
        padding: '20px',
        borderTop: '1px solid #999',
    },
    dialogOverlay: {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
    },
    dialog: {
        backgroundColor: Theme.Colours.workArea,
        borderRadius: '12px',
        padding: '32px',
        // Give the client access dialog more room for long support area labels.
        width: 'min(820px, calc(100vw - 48px))',
        maxWidth: '820px',
        maxHeight: '80vh',
        overflowY: 'auto' as const,
        border: Theme.Colours.border,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    },
    dialogTitle: {
        ...Theme.Fonts.h2,
        margin: '0 0 24px 0',
        color: Theme.Colours.text,
    },
    section: {
        border: `1px solid #E0E0E0`,
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        // backgroundColor: Theme.Colours.background,
    },
    sectionTitle: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        fontSize: '16px',
        color: Theme.Colours.text,
        margin: '0 0 16px 0',
    },
    formField: {
        marginBottom: '4px',
        display: 'flex',
        flexDirection: 'row' as const,
        alignItems: 'center',
        gap: '16px',
    },
    label: {
        ...Theme.Fonts.body,
        fontWeight: 'regular' as const,
        color: Theme.Colours.text,
        minWidth: '160px',
        textAlign: 'left' as const,
    },
    select: {
        ...Theme.Fonts.body,
        flex: 1,
        padding: '12px 16px 12px 16px', // top right bottom left
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
    },
    speechNeedsCheckboxList: {
        flex: 1,
        minWidth: '220px',
        marginLeft: '16px',
        maxHeight: '180px',
        overflowY: 'auto' as const,
        padding: '8px 12px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        boxSizing: 'border-box' as const,
    },
    speechNeedCheckboxLabel: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        minHeight: '34px',
        cursor: 'pointer',
    },
    input: {
        ...Theme.Fonts.body,
        flex: 1,
        padding: '12px 16px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        boxSizing: 'border-box' as const,
    },
    createClientPhoneInputGroup: {
        // Match the global input/select left margin used by the rest of this dialog.
        marginLeft: '16px',
    },
    dialogButtons: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '32px',
    },
    requiredFields: {
        ...Theme.Fonts.body,
        fontWeight: 'regular' as const,
        color: Theme.Colours.text,
        margin: '0 0 16px 0',
    },
    checkboxContainer: {
        marginBottom: '12px',
    },
    checkboxLabel: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        cursor: 'pointer',
    },
    checkboxLabelInline: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        cursor: 'pointer',
        minHeight: '46px',
    },
    checkbox: {
        marginLeft: '0px',
        width: '16px',
        height: '16px',
        flexShrink: 0,
    },
}
